import { telemetry } from '../backend';
import checkCapacitorAndPluginsAvailable from '../helper-functions';
import captureSpecificError from '../capture-error';

/*
  All methods below check for cordova & the existence of the plugin,
  upon failure they will error out, which is to be handled by the consumer.

  Upon success, most methods do NOT return anything.
*/

let deckeeDatabase;
let cachedSQLLiteAvailableCheck;

/**
 * @param  {Object} queryResponse
 * @return {array} returns with an array of rows from the query response
 */
const constructArrayFromQuery = (queryResponse) => {
  const rows = [];
  for (let x = 0; x < queryResponse.rows.length; x += 1) {
    rows.push(queryResponse.rows.item(x));
  }
  return rows;
};

/**
 * Automatically verify basic database access operations including opening a database
 * @return {function} will return a promise that resolves with true, or an error depending if the plugin exists
 */
const verifyDatabaseAccess = checkCapacitorAndPluginsAvailable(
  ['sqlitePlugin'],
  () =>
    new Promise((resolve, reject) => {
      try {
        // eslint-disable-next-line no-undef
        sqlitePlugin.selfTest(() => {
          resolve(true);
        }, reject);
      } catch (error) {
        reject(error);
      }
    }),
);

export const isSQLLiteAvailable = async () => {
  if (typeof cachedSQLLiteAvailableCheck === 'undefined') {
    try {
      cachedSQLLiteAvailableCheck = await verifyDatabaseAccess();
    } catch (error) {
      cachedSQLLiteAvailableCheck = false;
    }
  }

  return cachedSQLLiteAvailableCheck;
};

export const isDeckeeDatabaseAvailable = () => !!deckeeDatabase;

/**
 * @param  {string} query - Query that is executed against the database
 * @param  {Array<any>} values - Values to be injected into the SQL query via parameters
 * @return {Promise<object>} Will return a promise that rejects with an error, and resolves with an array of rows from the query
 */
const queryDatabase = async (query, values = []) =>
  new Promise((resolve, reject) => {
    let res;

    try {
      deckeeDatabase.transaction(
        (transaction) => {
          transaction.executeSql(
            query,
            values,
            (tx, result) => {
              res = result;
            },
            (error) => {
              reject(error);
            },
          );
        },
        (error) => {
          reject(error);
        },
        () => {
          telemetry(telemetry.DEBUG, `[database.queryDatabase] transaction OK`);
          if (query.includes('SELECT')) {
            res = constructArrayFromQuery(res);
          }
          resolve(res);
        },
      );
    } catch (e) {
      try {
        captureSpecificError(
          e,
          'containers/app-main-client/src/services/sql-lite/index.js',
          {
            errorInfo: e.message,
          },
        );
      } catch (er) {
        // eslint-disable-next-line no-console
        console.error('Could not log error', er, e);
      }
    }
  });

/**
 * @param  {string} name - name of the database to be opened
 * @returns {Promise<object>} a promise that rejects with an error, or resolves with a database object
 */
const openDatabase = (name) =>
  new Promise((resolve, reject) => {
    // eslint-disable-next-line no-undef
    const database = sqlitePlugin.openDatabase(
      {
        name: `${name}.db`,
        location: 'default',
      },
      (error) => {
        reject(error);
      },
    );
    resolve(database);
  });

/**
 * @param {string} tableName - name of the table to be created
 * @param {object} columns - object where key is column name, and value is column type
 */
export const createTable = checkCapacitorAndPluginsAvailable(
  ['sqlitePlugin'],
  async (tableName, columns) => {
    if (tableName.length > 30) {
      throw new Error('Table name must be less than 30 characters');
    }
    if (columns.length === 0) {
      throw new Error('Columns must be provided to create table');
    }

    // All tables come default with an automatically captured timestamp for records
    await queryDatabase(
      `CREATE TABLE IF NOT EXISTS "${tableName}" (
      ${Object.keys(columns)
        .map((columnName) => ` ${columnName} ${columns[columnName]}`)
        .join(',')},
      timestamp TEXT
      );`,
      [],
    );

    telemetry(
      telemetry.DEBUG,
      `[database.createTable] Successfully created table ${tableName}`,
    );
  },
);

/**
 * @param {string} tableName - name of table to insert records into
 * @param {array<string>} columns - an array of column names that are being inserted
 * @param {array<any>} values - an array of column values corresponding to the column names
 */
export const insertRecord = checkCapacitorAndPluginsAvailable(
  ['sqlitePlugin'],
  async (tableName, columns, values) => {
    telemetry(telemetry.DEBUG, `[database.insertRecord] insertRecord`);

    if (columns.length !== values.length) {
      throw new Error('[database.insertRecord] Column and value mismatch');
    }

    const result = await queryDatabase(
      `INSERT INTO 
                                ${tableName} (${columns
                                  .map((column) => `${column} `)
                                  .join(',')})
                                VALUES (${values.map(() => '?').join(',')})`,
      values,
    );

    telemetry(
      telemetry.DEBUG,
      `[database.insertRecord] Result ${JSON.stringify(result)}`,
    );
  },
);

export const getFirstNRecords = checkCapacitorAndPluginsAvailable(
  ['sqlitePlugin'],
  async (tableName, limit) => {
    if (!tableName) {
      throw new Error('[database.getFirstNRecords] table name missing');
    }

    if (!limit) {
      throw new Error('[database.getFirstNRecords] limit missing');
    }

    return queryDatabase(
      `SELECT * FROM ${tableName} ORDER BY timestamp ASC LIMIT ${limit}`,
    );
  },
);

export const clearEventsIfNotSession = checkCapacitorAndPluginsAvailable(
  ['sqlitePlugin'],
  async (sessionId) => {
    if (!sessionId) {
      throw new Error(
        '[database.clearEventsIfNotSession] no session if supplied',
      );
    }

    try {
      await queryDatabase(
        `DELETE FROM events WHERE sessionId != "${sessionId}" OR sessionId IS NULL`,
      );
    } catch (e) {
      telemetry(
        telemetry.WARN,
        `[database.clearEventsIfNotSession] unable to clear events ${e.message}`,
      );
    }
  },
);

export const deleteRecordsBeforeTimestamp = checkCapacitorAndPluginsAvailable(
  ['sqlitePlugin'],
  async (tableName, timestamp) => {
    if (!tableName) {
      throw new Error('[database.deleteFirstNRecords] table name missing');
    }

    if (!timestamp) {
      throw new Error('[database.deleteFirstNRecords] timestamp missing');
    }

    return queryDatabase(
      `DELETE FROM ${tableName} WHERE timestamp <= "${timestamp}"`,
    );
  },
);

export const getAllRecords = checkCapacitorAndPluginsAvailable(
  ['sqlitePlugin'],
  async (tableName) => {
    if (!tableName) {
      throw new Error('[database.getAllRecords] table name missing');
    }

    return queryDatabase(`SELECT * FROM ${tableName}`);
  },
);

export const deleteAllRecords = checkCapacitorAndPluginsAvailable(
  ['sqlitePlugin'],
  async (tableName) => {
    if (!tableName) {
      throw new Error('[database.deleteAllRecords] table name missing');
    }

    return queryDatabase(`DELETE FROM ${tableName}`);
  },
);

export const initialize = async () => {
  if (await isSQLLiteAvailable()) {
    deckeeDatabase = await openDatabase('deckee');
    telemetry(telemetry.DEBUG, '[sqlLite.initialize] Deckee database created');
  } else {
    telemetry(
      telemetry.DEBUG,
      `[sqlLite.initialize] Deckee database was not created`,
    );
  }
};
